import * as React from 'react';
import cx from 'classnames';

import { EditorState } from 'draft-js';
import { IMessageTemplate } from '@frontend/app/hooks';
import { InlineStyles } from './InlineStyles';
import { ListTypes } from './ListTypes';
import { MediaTypes } from './MediaTypes';
import { AdditionalTypes } from './AdditionalTypes';
import { FontControls } from './FontControls';
import { ColorControls } from './ColorControls';
import { AlignmentControls } from './AlignmentControls';

const EmojiPickerSingle = React.lazy(() => import('./EmojiPickerSingle').then((module) => ({ default: module.EmojiPickerSingle })));
import { UndoRedoControls } from './UndoRedoControls';
import {
 TBlockType, TInlineStyle, TMediaType, TFontFamily, TFontSize, TTextColor, THighlightColor, TTextAlign, TIndentType,
} from './types';

import styles from './StyleControls.module.scss';

interface IProps {
  editorState: EditorState;
  getSelectedEntityType(): string;
  toggleBlockType(type: TBlockType): void;
  toggleInlineStyle(style: TInlineStyle): void;
  toggleMediaType(type: TMediaType, ref?: React.RefObject<HTMLInputElement>): void;
  onFilesSelected(files: FileList): void;
  showProgramVariables: boolean;
  enableVariableReplacement?: boolean;
  enableEmailTemplate?: boolean;
  invalidMemberIdsForField: {
    [field: string]: number[];
  };
  additionalVariables?: { [key: string]: { label: string } };
  className?: string;
  onTemplateSelected?: (template: IMessageTemplate) => void;
  enableMessageTemplate?: boolean;
  currentFont?: TFontFamily;
  currentSize?: TFontSize;
  currentTextColor?: TTextColor;
  currentHighlightColor?: THighlightColor;
  currentAlignment?: TTextAlign;
  onFontChange?: (font: TFontFamily) => void;
  onSizeChange?: (size: TFontSize) => void;
  onTextColorChange?: (color: TTextColor) => void;
  onHighlightColorChange?: (color: THighlightColor) => void;
  onAlignmentChange?: (alignment: TTextAlign) => void;
  onIndentChange?: (type: TIndentType) => void;
  onEmojiSelect?: (emoji: string) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  emailV2?: boolean;
}

const StyleControlsComponent = (props: IProps, ref) => {
  const { editorState } = props;
  return (
    <div className={cx(styles.StyleControls, props.className)}>
      {/* Undo/Redo Controls - First Position */}
      {(props.emailV2 && (props.onUndo || props.onRedo)) && (
        <>
          <UndoRedoControls
            editorState={editorState}
            onUndo={props.onUndo || (() => {})}
            onRedo={props.onRedo || (() => {})}
          />
          <div className={styles.divider} />
        </>
      )}

      {/* Font and Color Controls */}
      {(props.emailV2 && (props.onFontChange || props.onSizeChange)) && (
        <>
          <FontControls
            editorState={editorState}
            currentFont={props.currentFont}
            currentSize={props.currentSize}
            onFontChange={props.onFontChange || (() => {})}
            onSizeChange={props.onSizeChange || (() => {})}
          />
          <div className={styles.divider} />
        </>
      )}

      {(props.emailV2 && (props.onTextColorChange || props.onHighlightColorChange)) && (
        <>
          <ColorControls
            currentTextColor={props.currentTextColor}
            currentHighlightColor={props.currentHighlightColor}
            onTextColorChange={props.onTextColorChange || (() => {})}
            onHighlightColorChange={props.onHighlightColorChange || (() => {})}
          />
          <div className={styles.divider} />
        </>
      )}

      {/* Basic Text Formatting */}
      <InlineStyles
        editorState={editorState}
        toggleInlineStyle={props.toggleInlineStyle}
        emailV2={props.emailV2}
      />
      <div className={styles.divider} />

      {/* Alignment and Indentation */}
      {(props.emailV2 && (props.onAlignmentChange || props.onIndentChange)) && (
        <>
          <AlignmentControls
            currentAlignment={props.currentAlignment}
            onAlignmentChange={props.onAlignmentChange || (() => {})}
            onIndentChange={props.onIndentChange || (() => {})}
          />
          <div className={styles.divider} />
        </>
      )}

      {/* Lists */}
      <ListTypes
        editorState={editorState}
        toggleBlockType={props.toggleBlockType}
      />
      <div className={styles.divider} />

      {/* Media Types */}
      <MediaTypes
        editorState={editorState}
        getSelectedEntityType={props.getSelectedEntityType}
        toggleMediaType={props.toggleMediaType}
        onFilesSelected={props.onFilesSelected}
        emailV2={props.emailV2}
      />
      <div className={styles.divider} />

      {/* Emoji Picker */}
      {props.onEmojiSelect && (
        <>
          {!props.emailV2 && <div className={styles.divider} />}
          <React.Suspense fallback={<div style={{ padding: '8px', fontSize: '12px', color: '#666' }}>Loading...</div>}>
            <EmojiPickerSingle
              onEmojiSelect={props.onEmojiSelect}
            />
          </React.Suspense>
        </>
      )}

      {/* Additional Types */}
      {(props.enableVariableReplacement || props.enableEmailTemplate) && (
        <>
          <div className={styles.divider} />
          <AdditionalTypes
            ref={ref}
            showProgramVariables={props.showProgramVariables}
            enableVariableReplacement={props.enableVariableReplacement}
            invalidMemberIdsForField={props.invalidMemberIdsForField}
            additionalVariables={props.additionalVariables}
            onTemplateSelected={props.onTemplateSelected}
            enableMessageTemplate={props.enableMessageTemplate}
          />
        </>
      )}
    </div>
  );
};

StyleControlsComponent.displayName = 'StyleControls';

export const StyleControls = React.forwardRef(StyleControlsComponent);
